import { useCallback, useEffect, useRef, useState } from 'react';
import { Icon } from './JournalOverview';
import { canShareExport, downloadExportBlob, exportFileSize, isAppleExportDevice, isTouchExportDevice, makeExportFile } from './share-file-delivery';
import './share-export-result.css';

export function useExportDelivery(download = downloadExportBlob) {
  const [result, setResult] = useState(null);
  const [sharing, setSharing] = useState(false);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const onSharingChange = useCallback(value => { if (mounted.current) setSharing(value); }, []);
  const prepare = (blob, filename) => {
    if (!mounted.current) return;
    const file = makeExportFile(blob, filename), mobile = isTouchExportDevice();
    // Rendering can outlast iOS user activation. Share only from the next real tap.
    setResult({ blob, file, filename, mobile, apple: isAppleExportDevice(), shareable: canShareExport(file), downloaded: !mobile });
    if (!mobile) download(blob, filename);
  };
  return { result, prepare, dismiss: () => setResult(null), download, sharing, onSharingChange };
}

export default function ShareExportResult({ delivery, disabled = false }) {
  const { result, dismiss, download, onSharingChange } = delivery;
  const [pending, setPending] = useState(false), [message, setMessage] = useState(''), [url, setURL] = useState('');
  const primary = useRef(null), mounted = useRef(true), sharing = useRef(false);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useEffect(() => () => onSharingChange(false), [onSharingChange]);
  useEffect(() => {
    setMessage('');
    if (!result) { setURL(''); return; }
    primary.current?.focus({ preventScroll: true });
    const href = URL.createObjectURL(result.blob); setURL(href);
    // Let an opened preview finish loading if the editor closes or replaces the export.
    return () => { setTimeout(() => URL.revokeObjectURL(href), 30000); };
  }, [result]);
  if (!result) return null;
  const extension = result.filename.split('.').pop().toLowerCase();
  const kind = extension === 'png' ? 'Photo' : extension === 'gif' ? 'GIF' : 'Video';
  const photosCompatible = ['png', 'gif', 'mp4'].includes(extension);
  const saveAction = extension === 'mp4' ? 'Save Video' : 'Save Image';
  const instruction = result.apple
    ? photosCompatible
      ? result.shareable ? `Tap Save or share, then choose ${saveAction} to add it to Photos. Downloads go to Files instead.` : `Download the file, then open it in Files and use Share → ${saveAction}.`
      : 'This video uses WebM. Download it to Files, or export an MP4 in Safari for Photos.'
    : result.shareable ? 'Save to your device or choose an app in the share sheet. Your export stays on this device until you choose where to send it.' : 'Download your file to keep it on your device. You can also open it before saving.';
  const share = async () => {
    if (sharing.current || disabled) return;
    sharing.current = true; setPending(true); onSharingChange(true); setMessage('');
    try {
      // No async work before this call: Safari needs the button's transient activation.
      await navigator.share({ files: [result.file] });
      if (mounted.current) setMessage('Share sheet closed. Your export is still ready if you need another copy.');
    } catch (error) {
      if (mounted.current) setMessage(error.name === 'AbortError' ? 'Sharing closed. Your export is still ready.' : 'The share sheet could not open. Try again, or use Download file.');
    } finally { sharing.current = false; onSharingChange(false); if (mounted.current) setPending(false); }
  };
  const save = () => {
    try { download(result.blob, result.filename); setMessage(result.apple ? 'Download started. Check Files → Downloads; this does not add it to Photos.' : 'Download started. Check your browser’s downloads.'); }
    catch { setMessage('The download could not start. Open the file and save it from your browser.'); }
  };
  return <section className="tl-export-result" aria-label="Export ready">
    <div className="tl-export-result-info"><span className="tl-export-result-mark" aria-hidden="true"><Icon name="check" size={20}/></span><div><strong>{kind} ready<span>{extension.toUpperCase()} · {exportFileSize(result.blob.size)}</span></strong><p>{instruction}</p></div></div>
    <div className="tl-export-result-actions">
      {result.shareable && <button ref={primary} type="button" className="tl-export-result-primary" disabled={disabled || pending} onClick={share}><Icon name="share" size={17}/>{pending ? 'Sharing…' : 'Save or share'}</button>}
      <button ref={result.shareable ? undefined : primary} type="button" className={result.shareable ? '' : 'tl-export-result-primary'} disabled={disabled || pending} onClick={save}><Icon name="download" size={17}/>{result.downloaded ? 'Download again' : 'Download file'}</button>
      {url && <a href={url} target="_blank" rel="noopener noreferrer" aria-disabled={pending || disabled} onClick={event => { if (pending || disabled) event.preventDefault(); }}>Open file</a>}
      <button type="button" className="tl-export-result-back" disabled={disabled || pending} onClick={dismiss}>Back to editing</button>
    </div>
    {message && <p className="tl-export-result-status" role="status">{message}</p>}
  </section>;
}
